import {Badge, Button, Popover, PopoverChevron, PopoverTrigger} from '@sollapay/ui/components';
import {useTranslation} from 'react-i18next';

import {useTrustTypeStrategy} from '@/features/trust-accounts/providers';

import {BuyerRegulatoryReportsPopover} from './BuyerRegulatoryReportsPopover';

import type {BuyerPurchaseDTO} from '@sollapay/types';
import type {ComponentProps, FC} from 'react';

type BadgeSize = NonNullable<ComponentProps<typeof Badge>['size']>;

/** Chevron scales with the badge; defaults preserve the existing size-4 look at the default `md`. */
const CHEVRON_SIZE: Record<BadgeSize, string> = {
  xs: 'size-3',
  sm: 'size-3.5',
  md: 'size-4',
  lg: 'size-4.5'
};

export interface BuyerRegulatoryReportsBadgeProps {
  purchase: BuyerPurchaseDTO;
  trustAccountId: string;
  size?: BadgeSize;
}

export const BuyerRegulatoryReportsBadge: FC<BuyerRegulatoryReportsBadgeProps> = ({
  purchase,
  trustAccountId,
  size = 'md'
}) => {
  const {t} = useTranslation('trustAccounts');
  const {showsRegulatoryReports} = useTrustTypeStrategy();
  const {realEstateTaxation, salesLawCommissioner} = purchase.regulatoryReports;
  const completedCount = (realEstateTaxation ? 1 : 0) + (salesLawCommissioner ? 1 : 0);
  const isComplete = completedCount === 2;

  if (!showsRegulatoryReports) return null;

  return (
    <Popover>
      <PopoverTrigger
        render={
          <Button
            variant="tertiary"
            className="h-auto cursor-pointer gap-1 p-0 hover:bg-transparent"
            onClick={e => e.stopPropagation()}
          >
            <Badge variant={isComplete ? 'success' : 'neutral'} size={size} dot={isComplete}>
              {t('view.buyers.regulatoryReports.count', {count: completedCount, total: 2})}
            </Badge>
            <PopoverChevron className={CHEVRON_SIZE[size]} />
          </Button>
        }
      />
      <BuyerRegulatoryReportsPopover
        purchaseId={purchase.id}
        trustAccountId={trustAccountId}
        regulatoryReports={purchase.regulatoryReports}
      />
    </Popover>
  );
};
